import type { CSSProperties } from 'react'
import type { SectionRow, SettingsRow } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { burstConfetti, heartShower } from '@/lib/celebrate'
import { createAudioContext, playBirthdayTune } from '@/lib/melody'

import {
    Flame,
    Heart,
    Mic,
    MicOff,
    PartyPopper,
    Wind
} from 'lucide-react'

import SectionHeading from '@/components/landing/section-heading'

const ALL_LIT = [true, true, true, true, true]
const BLOW_THRESHOLD = 0.16
const DRIPS = [
    { left: 6, height: 14 },
    { left: 19, height: 22 },
    { left: 33, height: 12 },
    { left: 47, height: 26 },
    { left: 61, height: 16 },
    { left: 75, height: 24 },
    { left: 88, height: 13 }
]

interface CakeGameProps {
    section?: SectionRow
    settings: SettingsRow | null
}

export default function CakeGame({ section, settings }: CakeGameProps) {
    const [lit, setLit] = useState<Array<boolean>>(ALL_LIT)
    const [listening, setListening] = useState(false)
    const [micNote, setMicNote] = useState<string | null>(null)
    const [celebrating, setCelebrating] = useState(false)

    const cakeRef = useRef<HTMLDivElement>(null)
    const audioRef = useRef<AudioContext | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
    const frameRef = useRef<number | null>(null)
    const stopTuneRef = useRef<(() => void) | null>(null)
    const timersRef = useRef<Array<number>>([])

    const allOut = lit.every(candle => !candle)

    const ensureAudio = (): AudioContext | null => {
        if (!audioRef.current) audioRef.current = createAudioContext()
        if (audioRef.current?.state === 'suspended') void audioRef.current.resume()

        return audioRef.current
    }

    const stopListening = () => {
        if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)

        frameRef.current = null
        sourceRef.current?.disconnect()
        sourceRef.current = null
        streamRef.current?.getTracks().forEach(track => track.stop())
        streamRef.current = null
        cakeRef.current?.style.setProperty('--wind', '0')
        setListening(false)
    }

    const blowOne = () => {
        setLit(current => {
            const index = current.indexOf(true)

            if (index === -1) return current

            const next = [...current]

            next[index] = false

            return next
        })
    }

    const blowCandle = (index: number) => {
        ensureAudio()
        setLit(current => current.map((candle, position) => position === index ? false : candle))
    }

    const blowAll = () => {
        ensureAudio()

        lit.forEach((candle, index) => {
            if (!candle) return

            timersRef.current.push(window.setTimeout(() => {
                setLit(current => current.map((value, position) => position === index ? false : value))
            }, index * 140))
        })
    }

    const startListening = async () => {
        if (listening) {
            stopListening()

            return
        }

        if (!navigator.mediaDevices?.getUserMedia) {
            setMicNote('Is browser mein mic nahi chal raha. Candles pe tap karke bujhao.')

            return
        }

        const context = ensureAudio()

        if (!context) {
            setMicNote('Is browser mein mic nahi chal raha. Candles pe tap karke bujhao.')

            return
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
            })

            if (context.state === 'suspended') await context.resume()

            const source = context.createMediaStreamSource(stream)
            const analyser = context.createAnalyser()
            const samples = new Uint8Array(1024)
            let loudFrames = 0
            let lastBlow = 0

            analyser.fftSize = 1024
            source.connect(analyser)
            streamRef.current = stream
            sourceRef.current = source
            setMicNote(null)
            setListening(true)

            const listen = () => {
                analyser.getByteTimeDomainData(samples)

                let sum = 0

                for (let index = 0; index < samples.length; index += 1) {
                    const value = (samples[index] - 128) / 128

                    sum += value * value
                }

                const level = Math.sqrt(sum / samples.length)
                const now = performance.now()

                cakeRef.current?.style.setProperty('--wind', Math.min(1, level * 4).toFixed(3))

                loudFrames = level > BLOW_THRESHOLD ? loudFrames + 1 : Math.max(0, loudFrames - 2)

                if (loudFrames >= 5 && now - lastBlow > 170) {
                    lastBlow = now
                    blowOne()
                }

                frameRef.current = requestAnimationFrame(listen)
            }

            frameRef.current = requestAnimationFrame(listen)
        }
        catch {
            setMicNote('Mic ki permission nahi mili. Koi baat nahi, candles pe tap karke bujhao.')
        }
    }

    const relight = () => {
        stopTuneRef.current?.()
        stopTuneRef.current = null
        setCelebrating(false)
        setLit(ALL_LIT)
    }

    useEffect(() => {
        if (!allOut || celebrating) return

        stopListening()
        setCelebrating(true)
        void burstConfetti()
        void heartShower()
        stopTuneRef.current = playBirthdayTune(audioRef.current)
    }, [allOut, celebrating])

    useEffect(() => {
        const timers = timersRef.current

        return () => {
            timers.forEach(timer => window.clearTimeout(timer))
            if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
            streamRef.current?.getTracks().forEach(track => track.stop())
            stopTuneRef.current?.()
            void audioRef.current?.close().catch(() => undefined)
        }
    }, [])

    return (
        <div id='cake' className='relative scroll-mt-8 overflow-hidden rounded-[2.5rem] bg-gradient-to-b from-white to-blush/70 px-6 py-12 shadow-[0_30px_70px_-40px_rgba(91,21,48,0.5)] ring-1 ring-blush sm:px-10 sm:py-14'>
            <SectionHeading section={section} level='h3' size='md' />

            <div
                ref={cakeRef}
                className='relative mx-auto mt-12 w-[17rem] sm:w-[19rem]'
                style={{ '--wind': 0 } as CSSProperties}
            >
                <div className='relative z-10 flex h-24 items-end justify-center gap-4 sm:gap-5'>
                    {lit.map((isLit, index) => (
                        <button
                            key={index}
                            type='button'
                            onClick={() => blowCandle(index)}
                            disabled={!isLit}
                            aria-label={isLit ? `Candle ${index + 1} bujhao` : `Candle ${index + 1} bujh gayi`}
                            className='relative flex h-full w-6 items-end justify-center rounded-md outline-none focus-visible:ring-4 focus-visible:ring-petal/40 disabled:cursor-default'
                        >
                            <span className='relative h-14 w-3.5 rounded-t-[3px] bg-[repeating-linear-gradient(135deg,#ffffff_0_6px,#f7a1b8_6px_12px)] shadow-[inset_-2px_0_0_rgba(0,0,0,0.08)]'>
                                <span className='absolute -top-1.5 left-1/2 h-2 w-0.5 -translate-x-1/2 rounded-full bg-ink' />

                                {isLit && (
                                    <span
                                        aria-hidden
                                        className='absolute bottom-full left-1/2 mb-1 -translate-x-1/2 origin-bottom transition-transform duration-100'
                                        style={{ transform: 'skewX(calc(var(--wind, 0) * -24deg))' }}
                                    >
                                        <span className='block h-7 w-4 origin-bottom animate-flicker rounded-[50%_50%_45%_45%/65%_65%_35%_35%] bg-[radial-gradient(circle_at_50%_72%,#fffbe0_0%,#ffd166_38%,#ff9a3d_72%,rgba(255,110,60,0)_100%)] shadow-[0_0_18px_6px_rgba(255,190,90,0.55)] motion-reduce:animate-none' />
                                    </span>
                                )}

                                {!isLit && (
                                    <span aria-hidden className='absolute bottom-full left-1/2 mb-1 size-4 -translate-x-1/2 animate-smoke rounded-full bg-[#d9d2d6]/80 blur-[3px]' />
                                )}
                            </span>
                        </button>
                    ))}
                </div>

                <div aria-hidden className='relative mx-auto h-16 w-[12.5rem] rounded-t-2xl bg-gradient-to-b from-[#fff6f8] to-[#fbd3de] shadow-[inset_0_-6px_12px_rgba(194,45,90,0.12)] sm:w-[14rem]'>
                    <span className='absolute inset-x-0 top-0 h-3 rounded-t-2xl bg-white' />

                    {DRIPS.map(drip => (
                        <span
                            key={drip.left}
                            className='absolute top-2 w-3.5 rounded-b-full bg-white'
                            style={{ left: `${drip.left}%`, height: drip.height }}
                        />
                    ))}

                    <span className='absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2'>
                        <span className='size-1.5 rounded-full bg-petal/70' />
                        <span className='size-1.5 rounded-full bg-gold' />
                        <span className='size-1.5 rounded-full bg-petal/70' />
                        <span className='size-1.5 rounded-full bg-gold' />
                        <span className='size-1.5 rounded-full bg-petal/70' />
                    </span>
                </div>

                <div aria-hidden className='relative mx-auto h-24 w-full rounded-t-xl rounded-b-2xl bg-gradient-to-b from-petal to-petal-deep shadow-[inset_0_-10px_18px_rgba(91,21,48,0.25)]'>
                    <span className='absolute inset-x-0 top-0 h-3 rounded-t-xl bg-[#fff6f8]' />

                    {DRIPS.map(drip => (
                        <span
                            key={drip.left}
                            className='absolute top-2 w-4 rounded-b-full bg-[#fff6f8]'
                            style={{ left: `${(drip.left + 4) % 96}%`, height: drip.height + 4 }}
                        />
                    ))}

                    <span className='absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-3'>
                        {[0, 1, 2, 3, 4].map(heart => (
                            <Heart key={heart} className='size-4 fill-cream/80 text-cream/80' />
                        ))}
                    </span>
                </div>

                <div aria-hidden className='-mx-[9%] -mt-1.5 h-5 w-[118%] rounded-[50%] bg-gradient-to-b from-white to-[#eadde2] shadow-[0_18px_30px_-12px_rgba(91,21,48,0.45)]' />
            </div>

            <div className='mx-auto mt-10 flex max-w-md flex-col items-center text-center' aria-live='polite'>
                {celebrating
                    ? (
                        <div className='flex animate-in flex-col items-center duration-700 fade-in slide-in-from-bottom-3'>
                            <PartyPopper aria-hidden className='size-8 text-petal-deep' />

                            {settings?.cake_message && (
                                <p
                                    data-mdlfy-cms={`settings:${settings.id}:cake_message`}
                                    className='mt-3 font-heading text-xl leading-snug text-balance text-wine italic sm:text-2xl'
                                >
                                    {settings.cake_message}
                                </p>
                            )}

                            <button
                                type='button'
                                onClick={relight}
                                className='mt-6 inline-flex items-center gap-2 rounded-full bg-wine px-5 py-2.5 text-sm font-bold text-cream transition outline-none hover:bg-petal-deep focus-visible:ring-4 focus-visible:ring-petal/40'
                            >
                                <Flame aria-hidden className='size-4' />
                                Candles phir se jalao
                            </button>
                        </div>
                    )
                    : (
                        <>
                            <div className='flex flex-wrap justify-center gap-3'>
                                <button
                                    type='button'
                                    onClick={() => void startListening()}
                                    aria-pressed={listening}
                                    className={cn(
                                        'inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-bold shadow-sm transition outline-none focus-visible:ring-4 focus-visible:ring-petal/40',
                                        listening ? 'bg-petal-deep text-white' : 'bg-gradient-to-r from-petal to-petal-deep text-white hover:brightness-110'
                                    )}
                                >
                                    {listening
                                        ? <MicOff aria-hidden className='size-4' />
                                        : <Mic aria-hidden className='size-4' />}
                                    {listening ? 'Mic band karo' : 'Mic se phoonko'}
                                </button>

                                <button
                                    type='button'
                                    onClick={blowAll}
                                    className='inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold text-wine shadow-sm ring-1 ring-blush transition outline-none hover:bg-blush focus-visible:ring-4 focus-visible:ring-petal/40'
                                >
                                    <Wind aria-hidden className='size-4' />
                                    Ek saath phoonko
                                </button>
                            </div>

                            <p className={cn('mt-4 text-sm', listening ? 'font-semibold text-petal-deep' : 'text-muted-foreground')}>
                                {listening
                                    ? 'Sun raha hoon... ab zor se phoonko!'
                                    : micNote ?? 'Ya kisi bhi candle pe tap karke use bujhao.'}
                            </p>
                        </>
                    )}
            </div>
        </div>
    )
}
